import { useEffect, useRef, useState } from "react";

interface MultiSelectFilterDropdownProps {
  label: string;
  allOptions: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  allLabel?: string;
  searchPlaceholder?: string;
}

export function MultiSelectFilterDropdown({
  label,
  allOptions,
  selected,
  onChange,
  allLabel,
  searchPlaceholder = "Search…",
}: MultiSelectFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && openRef.current) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const filteredOptions = q
    ? allOptions.filter((o) => o.toLowerCase().includes(q))
    : allOptions;

  const allSelected =
    allOptions.length > 0 && selected.size === allOptions.length;
  const isFiltered = selected.size < allOptions.length;

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selected.has(o));

  const toggle = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange(next);
  };

  const toggleFiltered = () => {
    const next = new Set(selected);
    if (allFilteredSelected) {
      filteredOptions.forEach((o) => next.delete(o));
    } else {
      filteredOptions.forEach((o) => next.add(o));
    }
    onChange(next);
  };

  const selectedNames = allOptions.filter((o) => selected.has(o));
  const summary =
    selected.size === 0
      ? "None selected"
      : allSelected
        ? (allLabel ?? `All ${label.toLowerCase()}`)
        : selectedNames.length <= 3
          ? selectedNames.join(", ")
          : `${selected.size} of ${allOptions.length} selected`;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${label} filter`}
        className="form-select d-flex align-items-center"
        style={{
          fontSize: "0.925rem",
          color: isFiltered ? "var(--color-p2)" : "#0f1923",
          fontWeight: isFiltered ? 600 : 500,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 100,
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            minWidth: "260px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #e9ecef" }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={searchPlaceholder}
              aria-label={`Search ${label}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "8px 14px",
              borderBottom: "1px solid #e9ecef",
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#0f1923",
              }}
            >
              {label}
            </span>
            <button
              type="button"
              className="btn btn-link p-0"
              style={{
                fontSize: "0.8rem",
                color: "var(--color-p2)",
                textDecoration: "none",
              }}
              disabled={filteredOptions.length === 0}
              onClick={toggleFiltered}
            >
              {allFilteredSelected
                ? searching
                  ? `Deselect matches (${filteredOptions.length})`
                  : "Deselect All"
                : searching
                  ? `Select matches (${filteredOptions.length})`
                  : "Select All"}
            </button>
          </div>

          <div
            role="group"
            aria-label={`${label} options`}
            style={{ maxHeight: "240px", overflowY: "auto", padding: "6px 0" }}
          >
            {filteredOptions.length === 0 ? (
              <div
                className="text-muted text-center py-3"
                style={{ fontSize: "0.875rem" }}
              >
                No matches
              </div>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={option}
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: "7px 14px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: "#212529",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) =>
                    ((
                      e.currentTarget as HTMLLabelElement
                    ).style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    ((
                      e.currentTarget as HTMLLabelElement
                    ).style.backgroundColor = "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={selected.has(option)}
                    onChange={() => toggle(option)}
                    style={{
                      accentColor: "var(--color-p2)",
                      width: "15px",
                      height: "15px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  {option}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
